// The backend's own DeviceTypes table, in the app.
//
// ── Why this exists ────────────────────────────────────────────────────────
//
// Every product used to be three hand-written entries — a branch in
// `deviceTypeKey`, a row in `CHANNELS_BY_TYPE`, sometimes one in
// `FAN_CHANNELS_BY_TYPE` — and they had already drifted apart: DeviceIDs 1 and
// 87 had channel counts but no route, so a 4+1 and a 7+2 both landed on the
// generic "here are your outputs" fallback. Eight new glass panels (92–99)
// arrived in one batch, and there is no reason to believe that is the last one.
//
// The server already knows the answer. `GetAllDeviceTypeDetails` returns 100
// rows carrying exactly what the UI needs to draw a panel:
//
//     On_off   how many plain on/off tiles
//     Dimming  how many FAN channels follow them (a "4+1" is On_off 4, Dimming 1)
//     Outputs  the width of the status frame  (= On_off + Dimming)
//     DeviceType    the backend's own classification: Switch / Sensor / Curtain /
//                   ColorController / SmartLock / Fan / Other
//     TuyaMasterDp  set when the glass carries a master touch position
//
// So a new switch bank now needs no app release at all.
//
// ⚠️ `Outputs` ALONE IS NOT ENOUGH, which is the whole reason this reads the
// catalog rather than the device row. DeviceID 92 and 95 BOTH have Outputs 5:
// 92 is four switches plus a fan, 95 is five switches. Only On_off/Dimming
// separate them — and the room-device row (`GetDeviceDetailsbyUserIdAndRoomId`)
// carries `Outputs` and `Inputs` but NEITHER of those. Probed, not assumed.
//
// ⚠️ THE Tuya DP COLUMNS ARE READ, NEVER SENT. `TuyaMasterDp` tells us a master
// button exists; it does not tell us what to publish. The number is not even
// constant across products — 13 on the 4-touch and the 20A, 103 on the 4+1, 104
// on the 7+1 and the new glass panels — and the ESP8266 units in the field do
// not speak DPs at all. The master keeps sending `allon`/`alloff`, which both
// firmware generations parse. Same for the switches: `<n>-<0|1>`, and the ESP
// does the translation. See statusFormat.js and docs/11-tuya-mcu-protocol.md.

import { getDeviceTypes } from '../api/api';

const STORAGE_KEY = 'sli.deviceCatalog.v1';

// DeviceID -> entry. Module-level because `deviceTypeKey` and friends are plain
// synchronous functions called during render, all over both shells; threading a
// context through every one of them would be a far larger change than the
// problem warrants.
let CATALOG = Object.create(null);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * One row, reduced to what the UI actually asks.
 *
 * `consistent` is the guard that keeps this from guessing. Six rows in the live
 * table do not add up — Outputs != On_off + Dimming:
 *
 *     5  Siren(hooter)          out 1  on 0  dim 0
 *     19 Ir Blaster             out 0  on 1  dim 0
 *     35 Glass Sliding Door     out 1  on 0  dim 0
 *     39 Agricultural Sensors   out 7  on 2  dim 0
 *     57 Ir Receiver Transmiter out 0  on 1  dim 0
 *     62 Universal Ac Blaster   out 0  on 1  dim 0
 *
 * A row whose own numbers disagree cannot be turned into a panel honestly — a
 * 7-output device drawn as 2 tiles hides five loads. Those keep the generic
 * output-driven fallback, which reads OutputDetails and is right for them.
 */
function toEntry(row) {
  const deviceId = num(row.DeviceID ?? row.DeviceId);
  if (!deviceId && deviceId !== 0) return null;
  const outputs = num(row.Outputs);
  const onOff = num(row.On_off);
  const dimming = num(row.Dimming);
  return {
    deviceId,
    name: String(row.DeviceName ?? '').trim(),
    // Blank on three rows; '' is a real answer meaning "unclassified".
    type: String(row.DeviceType ?? '').trim(),
    outputs,
    onOff,
    dimming,
    // A master is NOT a channel — it is never in a status frame. See the long
    // note on MASTER_CHANNELS_BY_TYPE in statusFormat.js.
    hasMaster: num(row.TuyaMasterDp) > 0,
    consistent: outputs === onOff + dimming,
    // The GLASS SERIES (core/devices/glassPlates). null, not 0, while the
    // columns do not exist yet — so glassPlates can tell "not glass" from
    // "not migrated" and use its fallback list only for the latter.
    glassModules: 'GlassModules' in row ? num(row.GlassModules) : null,
    glassMaster: num(row.GlassMaster) > 0,
    glassSocket: num(row.GlassSocket) > 0,
  };
}

function store(entries) {
  CATALOG = entries;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(entries)));
  } catch (e) { /* private mode — the catalog just re-fetches next launch */ }
}

/**
 * Seed synchronously from the last successful load.
 *
 * ⚠️ WITHOUT THIS, THE FIRST PAINT AFTER EVERY LAUNCH IS WRONG. The fetch is a
 * round trip; the room grid renders long before it lands, and a panel drawn as
 * "unsupported" and then swapped for real buttons is a visible flicker on every
 * cold start — on a phone, over a slow link, a long one.
 */
(function seedFromStorage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const rows = JSON.parse(raw);
    if (!Array.isArray(rows)) return;
    const next = Object.create(null);
    rows.forEach((r) => { if (r && r.deviceId != null) next[r.deviceId] = r; });
    CATALOG = next;
  } catch (e) { /* corrupt or unavailable — we simply have no seed */ }
}());

/** What the backend says about this DeviceID, or null if it is not loaded. */
export const deviceCatalogEntry = (deviceId) => CATALOG[Number(deviceId)] || null;

/** How many types are known — 0 means every caller is on its fallback. */
export const deviceCatalogSize = () => Object.keys(CATALOG).length;

/**
 * Fetch the catalog. Call once at startup; failure is not fatal.
 *
 * -> how many types were loaded (0 on failure, leaving any seed in place).
 *
 * ⚠️ A FAILED LOAD MUST NOT EMPTY WHAT WE HAVE. The hardcoded tables still
 * cover every product that was shipping before this existed, and the seed
 * covers the rest — replacing either with {} because one request timed out
 * would turn working panels into "unsupported" while offline.
 */
export async function loadDeviceCatalog() {
  try {
    const res = await getDeviceTypes();
    const rows = Array.isArray(res && res.data) ? res.data : [];
    if (!rows.length) return 0;
    const next = Object.create(null);
    rows.forEach((r) => {
      const e = toEntry(r);
      if (e) next[e.deviceId] = e;
    });
    store(next);
    return Object.keys(next).length;
  } catch (e) {
    return 0;
  }
}

/** Load a catalog directly — for tests, and for seeding from an existing fetch. */
export function primeDeviceCatalog(rows) {
  const next = Object.create(null);
  (rows || []).forEach((r) => {
    const e = toEntry(r);
    if (e) next[e.deviceId] = e;
  });
  CATALOG = next;
  return Object.keys(next).length;
}

/** Forget everything — tests only, so one case cannot leak into the next. */
export function resetDeviceCatalog() {
  CATALOG = Object.create(null);
}
